import { useState, useCallback, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { BridgeEvent, NativeBridgeAvailable } from "@unu/ui-enums";
import { subscribe, unsubscribe } from "@unu/ui-common";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import Panel from "@/features/Common/components/Panel";
import IconLoader from "@/features/Common/components/IconLoader";
import { defaultFullscreenPageStyling } from "@/theme";
import { useNativeHealthSDK } from "@/hooks/useNativeHealthSDK";
import type { HealthRecord, HealthStatus } from "@/hooks/useNativeHealthSDK";
import {
  getLastSyncTimestamp,
  transformRecordsToPayload,
} from "@/hooks/useHealthDataSync";
import {
  submitMobileHealthData,
  type HealthRecordDTO,
} from "@/services/core-api-adapter";
import { clearValue } from "@/store/persistence";
import { ALL_SDK_DATA_TYPES } from "@/health/healthDataTypes";

interface LogEntry {
  timestamp: string;
  message: string;
}

function formatTime(): string {
  return new Date().toLocaleTimeString("en-GB", { hour12: false });
}

function groupByField(
  records: Record<string, unknown>[],
  field: string
): string {
  const groups: Record<string, number> = {};
  for (const r of records) {
    const key = String(r[field] ?? "unknown");
    groups[key] = (groups[key] || 0) + 1;
  }
  return Object.entries(groups)
    .map(([k, v]) => `${k}: ${v}`)
    .join(", ");
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}

interface NativeSdkIssuePanelProps {
  error: { code?: string; message?: string } | null | undefined;
  isAvailable: boolean;
  isHealthConnect: boolean;
  status: HealthStatus | null;
}

function getNativeSdkIssueMessage({
  error,
  isHealthConnect,
}: Pick<NativeSdkIssuePanelProps, "error" | "isHealthConnect">): string {
  if (error?.code === "ERR_NATIVE_MODULE_NOT_FOUND") {
    const platform = isHealthConnect ? "Health Connect" : "HealthKit";
    return `The native ${platform} module was not compiled into this build. This usually means the native source files (ios/ or android/ in packages/health-sdk) were excluded during the EAS build archive step. Check .easignore for overly broad patterns like "ios/" or "android/" that may strip SDK source directories.`;
  }
  if (error?.code === "ERR_PLATFORM_UNAVAILABLE") {
    return `The health platform is not available on this device. ${error.message}`;
  }
  return `Health platform reports unavailable. Error: ${error?.message ?? "unknown"}`;
}

function NativeSdkIssuePanel({
  error,
  isAvailable,
  isHealthConnect,
  status,
}: NativeSdkIssuePanelProps) {
  const shouldRender =
    error?.code === "ERR_NATIVE_MODULE_NOT_FOUND" ||
    error?.code === "ERR_PLATFORM_UNAVAILABLE" ||
    (!isAvailable && NativeBridgeAvailable && status !== null);

  if (!shouldRender) {
    return null;
  }

  return (
    <Panel>
      <Stack spacing={1}>
        <Typography variant="h4" color="error">
          Native SDK Issue Detected
        </Typography>
        <Typography
          variant="body2"
          sx={{
            bgcolor: "error.light",
            color: "error.contrastText",
            p: 1,
            borderRadius: 1,
            fontFamily: "monospace",
            fontSize: 12,
          }}
        >
          {getNativeSdkIssueMessage({ error, isHealthConnect })}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", fontSize: 11 }}
        >
          Error Code: {error?.code ?? "N/A"}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", fontSize: 11 }}
        >
          Bridge: {NativeBridgeAvailable ? "OK" : "Missing"} | Platform:{" "}
          {status?.platform ?? "none"} | Available: {String(isAvailable)} |
          Build: {status?.nativeAppVersion ?? "?"}.
          {status?.nativeBuildNumber ?? "?"}
        </Typography>
      </Stack>
    </Panel>
  );
}

interface DataSectionProps {
  title: string;
  data: unknown[];
  groupKey?: string;
}

function DataSection({ title, data, groupKey }: DataSectionProps) {
  const [copyLabel, setCopyLabel] = useState("Copy");

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await copyToClipboard(JSON.stringify(data, null, 2));
    setCopyLabel("Copied!");
    setTimeout(() => setCopyLabel("Copy"), 2000);
  };

  return (
    <Accordion defaultExpanded={false} disableGutters>
      <AccordionSummary
        expandIcon={<IconLoader icon="ChevronDownIcon" fontSize="medium" />}
        sx={{ py: 1 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", mr: 1 }}
        >
          <Typography variant="h4">
            {title} ({data.length})
          </Typography>
          <Button
            size="small"
            onClick={handleCopy}
            disabled={data.length === 0}
          >
            {copyLabel}
          </Button>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {data.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No data yet. Run &quot;Test Sync Push&quot; to populate.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {groupKey && (
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", fontSize: 12 }}
              >
                {groupByField(data as Record<string, unknown>[], groupKey)}
              </Typography>
            )}
            <Stack
              sx={{
                maxHeight: 300,
                overflow: "auto",
                bgcolor: "grey.100",
                borderRadius: 1,
                p: 1,
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  m: 0,
                }}
              >
                {JSON.stringify(data.slice(0, 3), null, 2)}
                {data.length > 3 && `\n... and ${data.length - 3} more`}
              </Typography>
            </Stack>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default function HealthKitDebug() {
  const {
    status,
    isAvailable,
    isConnected,
    permissions,
    records,
    isFetching,
    error,
    isSamsungHealthInstalled,
    requestPermissions,
    fetchHealthData,
    clearRecords,
    refreshStatus,
    openHealthConnectSettings,
    openAppPermissionSettings,
    revokeAllPermissions,
  } = useNativeHealthSDK();

  const isHealthConnect = status?.platform === "health_connect";

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isSyncRunning, setIsSyncRunning] = useState(false);
  const [copyLabel, setCopyLabel] = useState("Copy");
  const [normalizedSnapshot, setNormalizedSnapshot] = useState<HealthRecord[]>(
    []
  );
  const [dtoSnapshot, setDtoSnapshot] = useState<HealthRecordDTO[]>([]);
  const [lastSyncDisplay, setLastSyncDisplay] = useState<string>(
    () => getLastSyncTimestamp() ?? "Never"
  );
  const logsEndRef = useRef<HTMLDivElement>(null);

  const refreshLastSync = useCallback(() => {
    setLastSyncDisplay(getLastSyncTimestamp() ?? "Never");
  }, []);

  const addLog = useCallback((message: string) => {
    setLogs((prev) => [...prev, { timestamp: formatTime(), message }]);
    setTimeout(() => {
      logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, []);

  const handleCopyLogs = useCallback(async () => {
    const text = logs
      .map((entry) => `[${entry.timestamp}] ${entry.message}`)
      .join("\n");
    await copyToClipboard(text);
    setCopyLabel("Copied!");
    setTimeout(() => setCopyLabel("Copy"), 2000);
  }, [logs]);

  const handleClearHealthKitData = useCallback(() => {
    clearRecords();
    clearValue("healthDataLastSyncTimestamp");
    addLog("Cleared local health records and sync timestamp");
    refreshLastSync();
  }, [clearRecords, addLog, refreshLastSync]);

  const handleRequestPermissions = useCallback(() => {
    addLog("Requesting permissions for all health data types...");
    requestPermissions(ALL_SDK_DATA_TYPES);
  }, [requestPermissions, addLog]);

  const handleClearLastSyncTime = useCallback(() => {
    clearValue("healthDataLastSyncTimestamp");
    addLog("Cleared last sync timestamp from localStorage");
    refreshLastSync();
    refreshStatus();
  }, [addLog, refreshLastSync, refreshStatus]);

  const handleTestSyncPush = useCallback(async () => {
    if (isSyncRunning) {
      return;
    }
    setIsSyncRunning(true);

    try {
      const lastSync = getLastSyncTimestamp();
      addLog(`Last sync timestamp: ${lastSync ?? "none"}`);

      addLog("Fetching health data from native bridge...");
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);

      addLog("Waiting for bridge response (up to 15s)...");
      const fetchedRecords = await new Promise<HealthRecord[]>((resolve) => {
        const timeout = setTimeout(() => {
          unsubscribe({
            name: BridgeEvent.HealthDataReceived,
            context: "debug",
          });
          addLog("Bridge timeout after 15s");
          console.error("Bridge timeout after 15s", null, {
            phase: "debugSyncPush",
            reason: "bridge_timeout",
            isAvailable,
            isConnected,
          });
          resolve([]);
        }, 15000);

        subscribe({
          name: BridgeEvent.HealthDataReceived,
          context: "debug",
          callback: (data: any) => {
            clearTimeout(timeout);
            unsubscribe({
              name: BridgeEvent.HealthDataReceived,
              context: "debug",
            });
            resolve(data?.records ?? []);
          },
        });

        fetchHealthData({
          dataTypes: ALL_SDK_DATA_TYPES,
          startDate,
          endDate,
        });
      });

      addLog(`Received ${fetchedRecords.length} records from bridge`);
      setNormalizedSnapshot(fetchedRecords);

      if (fetchedRecords.length === 0) {
        addLog("No records to sync - done");
        setDtoSnapshot([]);
        return;
      }

      const payload = transformRecordsToPayload(fetchedRecords);
      addLog(`Transformed to ${payload.length} DTO records`);
      setDtoSnapshot(payload);

      addLog("Submitting to backend...");
      const response = await submitMobileHealthData(payload);
      addLog(`Backend response: ${JSON.stringify(response)}`);
      refreshLastSync();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : JSON.stringify(err);
      addLog(`Sync error: ${message}`);
      console.error("Debug sync push failed", err, {
        phase: "debugSyncPush",
        reason: "exception",
        isAvailable,
        isConnected,
      });
    } finally {
      setIsSyncRunning(false);
    }
  }, [
    isSyncRunning,
    fetchHealthData,
    addLog,
    refreshLastSync,
    isAvailable,
    isConnected,
  ]);

  const grantedCount =
    permissions?.dataTypes.filter((d) => d.read === "authorized").length ?? 0;
  const totalCount = permissions?.dataTypes.length ?? 0;

  const handleOpenHealthConnectSettings = useCallback(() => {
    openHealthConnectSettings();
  }, [openHealthConnectSettings]);

  const handleOpenAppPermissionSettings = useCallback(() => {
    openAppPermissionSettings();
  }, [openAppPermissionSettings]);

  const handleRevokeAllPermissions = useCallback(() => {
    revokeAllPermissions();
  }, [revokeAllPermissions]);

  return (
    <Stack spacing={3} sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton showLabel={true} />
      <Stack my={3}>
        <Typography variant="h2">
          {isHealthConnect ? "Health Connect Debug" : "HealthKit Debug"}
        </Typography>
      </Stack>

      <NativeSdkIssuePanel
        error={error}
        isAvailable={isAvailable}
        isHealthConnect={isHealthConnect}
        status={status}
      />

      <Panel>
        <Stack spacing={1}>
          <Typography variant="h4">Status</Typography>
          <Typography variant="body2">
            Native Bridge: {NativeBridgeAvailable ? "Available" : "Unavailable"}
          </Typography>
          <Typography variant="body2">
            Platform: {status?.platform ?? "unknown"}
          </Typography>
          <Typography variant="body2">
            Available: {isAvailable ? "Yes" : "No"}
          </Typography>
          <Typography variant="body2">
            Connected: {isConnected ? "Yes" : "No"}
          </Typography>
          {isHealthConnect && (
            <Typography variant="body2">
              Samsung Health:{" "}
              {isSamsungHealthInstalled ? "Installed" : "Not detected"}
            </Typography>
          )}
          <Typography variant="body2">
            Native App Version: {status?.nativeAppVersion ?? "N/A"}
          </Typography>
          <Typography variant="body2">
            Native Build Number: {status?.nativeBuildNumber ?? "N/A"}
          </Typography>
          <Typography variant="body2">
            Web App Version: {import.meta.env.VITE_APP_VERSION ?? "N/A"}
          </Typography>
          <Typography variant="body2">
            Binah SDK Version: {status?.binahNativeSdkVersion ?? "N/A"}
          </Typography>
          <Typography variant="body2">
            Permissions: {grantedCount}/{totalCount} granted
          </Typography>
          {error && (
            <Typography variant="body2" color="error">
              Error: {error.code} — {error.message}
            </Typography>
          )}
          <Typography variant="body2">Last Sync: {lastSyncDisplay}</Typography>
          <Typography variant="body2">
            Records in memory: {records.length}
          </Typography>
          <Typography variant="body2">
            Fetching: {isFetching ? "Yes" : "No"}
          </Typography>
        </Stack>
      </Panel>

      <Stack spacing={2}>
        <Typography variant="h4">Actions</Typography>
        <Button variant="outlined" onClick={handleClearHealthKitData} fullWidth>
          Clear HealthKit Data
        </Button>
        <Button variant="outlined" onClick={handleRequestPermissions} fullWidth>
          Request HealthKit Permissions
        </Button>
        <Button variant="outlined" onClick={handleClearLastSyncTime} fullWidth>
          Clear Last Sync Time
        </Button>
        <Button
          variant="contained"
          onClick={handleTestSyncPush}
          disabled={isSyncRunning}
          fullWidth
        >
          {isSyncRunning ? "Syncing..." : "Test Sync Push to Backend"}
        </Button>
      </Stack>

      {isHealthConnect && (
        <Stack spacing={2}>
          <Typography variant="h4">Health Connect</Typography>
          <Button
            variant="outlined"
            onClick={handleOpenHealthConnectSettings}
            fullWidth
          >
            Open Health Connect Settings
          </Button>
          <Button
            variant="outlined"
            onClick={handleOpenAppPermissionSettings}
            fullWidth
          >
            Open App Permission Settings
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleRevokeAllPermissions}
            fullWidth
          >
            Revoke All Permissions
          </Button>
        </Stack>
      )}

      <Accordion defaultExpanded={false} disableGutters>
        <AccordionSummary
          expandIcon={<IconLoader icon="ChevronDownIcon" fontSize="medium" />}
          sx={{ py: 1 }}
        >
          <Typography variant="h4">
            Permissions Detail ({grantedCount}/{totalCount})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {totalCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No permission data available. Request permissions first.
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {permissions?.dataTypes.map((perm) => (
                <Typography
                  key={perm.type}
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 12,
                    color:
                      perm.read === "authorized"
                        ? "success.main"
                        : perm.read === "denied" || perm.read === "restricted"
                          ? "error.main"
                          : "text.secondary",
                  }}
                >
                  {perm.type}: {perm.read}
                </Typography>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <Panel>
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4">Log</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={handleCopyLogs}
                disabled={logs.length === 0}
              >
                {copyLabel}
              </Button>
              <Button
                size="small"
                onClick={() => setLogs([])}
                disabled={logs.length === 0}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
          <Stack
            sx={{
              maxHeight: 300,
              overflow: "auto",
              bgcolor: "grey.100",
              borderRadius: 1,
              p: 1,
              fontFamily: "monospace",
              fontSize: 12,
            }}
          >
            {logs.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "monospace", fontSize: 12 }}
              >
                No log entries yet. Press an action button above.
              </Typography>
            ) : (
              logs.map((entry, i) => (
                <Typography
                  key={i}
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  [{entry.timestamp}] {entry.message}
                </Typography>
              ))
            )}
            <div ref={logsEndRef} />
          </Stack>
        </Stack>
      </Panel>

      <Accordion defaultExpanded={false} disableGutters>
        <AccordionSummary
          expandIcon={<IconLoader icon="ChevronDownIcon" fontSize="medium" />}
          sx={{ py: 1 }}
        >
          <Typography variant="h4">Raw Records</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Raw records are logged to the native console (Xcode/Logcat). SDK
            logger level is set to &quot;debug&quot; for QA builds. Look for
            &quot;[HealthSDK] [HealthPlatform] [DEBUG]&quot; entries.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <DataSection
        title="Normalized Records"
        data={normalizedSnapshot}
        groupKey="dataType"
      />

      <DataSection
        title="DTO Records"
        data={dtoSnapshot}
        groupKey="metricType"
      />
    </Stack>
  );
}
